import { IOperationResultNoValue, Industry } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { stringify as toQueryString } from 'query-string';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ILocationState } from '../../../../models';
import { ISystemAutomationTemplate } from '../../../../models/AdminModels';
import { useEventLogging } from '../../../../models/Logging';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import {
	useArchiveIndustryAutomationTemplateMutation,
	usePublishIndustryAutomationTemplateMutation,
} from '../../../../queries/Admin/AutomationTemplate';
import {
	IndustryAutomationTemplateData,
	useIndustryAutomationTemplatesData,
} from '../../../../queries/Admin/AutomationTemplate/useIndustryAutomationTemplatesData';
import { DuplicateSystemAutomationTemplateModal } from '../../../components/templates/DuplicateSystemAutomationTemplateModal';
import { TemplatesByIndustry } from '../../../components/templates/TemplatesByIndustry';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	className?: string;
	industry: Industry;
	styles?: StyleDeclarationValue[];
}

export const AutomationTemplatesByIndustry: React.FC<IProps> = props => {
	const { className, styles, industry } = props;
	const { logInput, logApiError, logPageView } = useEventLogging('AutomationTemplatesByIndustry');
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { postNotification } = useLocalNotificationService<ISystemAutomationTemplate>();

	const [templateToDuplicate, setTemplateToDuplicate] = React.useState<IndustryAutomationTemplateData | undefined>();

	const templatesData = useIndustryAutomationTemplatesData({ industry });
	const publishMutation = usePublishIndustryAutomationTemplateMutation();
	const archiveMutation = useArchiveIndustryAutomationTemplateMutation();

	React.useEffect(() => {
		logPageView(`${industry.split(' ').join()}AutomationTemplates`);
	}, [logPageView, industry]);

	const onDuplicateTemplateModalRequestClose = () => {
		setTemplateToDuplicate(null);
	};

	const onTemplateClicked = React.useCallback(
		(template: IndustryAutomationTemplateData) => {
			logInput('Edit', 'Click', { id: template.id, industry });
			const locationState: ILocationState<IndustryAutomationTemplateData, any> = { viewModel: template as any };
			fullscreenModal?.history?.push({
				pathname: `/automations/${template.id}`,
				search: toQueryString({ industry }),
				state: locationState,
			});
		},
		[fullscreenModal, logInput, industry]
	);

	const onMenuItemClicked = React.useCallback(
		(template: IndustryAutomationTemplateData, menuItem: 'edit' | 'archive' | 'delete' | 'publish' | 'copy') => {
			switch (menuItem) {
				case 'copy': {
					logInput('CopyAction', 'Click', { id: template.id, industry });
					setTemplateToDuplicate(template);
					break;
				}
				case 'edit': {
					logInput('EditAction', 'Click', { id: template.id, industry });
					const locationState: ILocationState<IndustryAutomationTemplateData, any> = { viewModel: template as any };
					fullscreenModal?.history?.push({
						pathname: `/automations/${template.id}`,
						search: toQueryString({ industry }),
						state: locationState,
					});
					break;
				}
				case 'publish': {
					logInput('PublishAction', 'Click', { id: template.id, industry });
					publishMutation.mutate(
						{
							templateId: template.id,
							industry,
							automationTemplate: template.template,
						},
						{
							onSuccess: publishedTemplateModel => {
								postNotification({
									info: publishedTemplateModel as any,
									topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
								});
							},
							onError: (error: IOperationResultNoValue) => {
								logApiError('PublishAction-Error', error);
								errorMessages.pushApiError(error);
							},
						}
					);
					break;
				}
				case 'delete': {
					// Note: Delete endpoint doesn't exist - using archive instead
					logInput('DeleteAction', 'Click', { id: template.id, industry });
					archiveMutation.mutate(
						{
							templateId: template.id,
							industry,
							automationTemplate: template.template,
						},
						{
							onError: (error: IOperationResultNoValue) => {
								logApiError('DeleteAction-Error', error);
								errorMessages.pushApiError(error);
							},
						}
					);
					break;
				}
				case 'archive': {
					logInput('ArchiveAction', 'Click', { id: template.id, industry });
					archiveMutation.mutate(
						{
							templateId: template.id,
							industry,
							automationTemplate: template.template,
						},
						{
							onError: (error: IOperationResultNoValue) => {
								logApiError('ArchiveAction-Error', error);
								errorMessages.pushApiError(error);
							},
						}
					);
					break;
				}
				default: {
					break;
				}
			}
		},
		[
			logInput,
			industry,
			fullscreenModal,
			publishMutation,
			archiveMutation,
			postNotification,
			logApiError,
			errorMessages,
		]
	);

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} automation-templates-by-industry ${className || ''}`}
		>
			<TemplatesByIndustry
				industry={industry}
				name='Automations'
				onMenuItemClicked={onMenuItemClicked as any}
				onTemplateClicked={onTemplateClicked as any}
				templates={templatesData as any}
			/>
			<DuplicateSystemAutomationTemplateModal
				industryAutomationTemplate={templateToDuplicate}
				isOpen={!!templateToDuplicate}
				onRequestClose={onDuplicateTemplateModalRequestClose}
			/>
		</div>
	);
};
